import React, { useRef, useEffect, useState } from 'react'
import { Modal, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { KTSVG } from '../../../../../_metronic/helpers'
import clsx from 'clsx'
import Select from 'react-select'
import { useSelector, useDispatch } from 'react-redux'
import { useFetchTagQuery } from '../../../../../services/cellcast.v3'
import { getalltagsdata } from '../../Contacts/Tags/TagSlice/TagsSlices'
import { getIsDefaultRegion } from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {
    bulkAssignConversations,
    bulkAddTagsToConversations,
    bulkMarkAsReadConversations,
    bulkCloseAsReadConversations,
    bulkUnsubscribeConversations,
    bulkArchiveConversations,
    bulkFlagConversations,
    bulkDeleteConversations,
    getSelectedConversations,
} from '../InboxSlice/InboxSlice'
import { useBulkOperations } from '../../../../hooks/useBulkOperations'
import styles from './BulkActionsDropdown.module.scss'

interface TeamMember {
    _id: string
    name: string
    email: string
    avatar?: string
}

interface BulkActionsDropdownProps {
    show: boolean
    onClose: () => void
    selectedCount: number
    totalCount: number
    onAssignTo: (assignee: TeamMember) => void
    onAddTag: (tags: string[]) => void
    onUnsubscribe: () => void
    onArchive: () => void
    onFlag: () => void
    onMarkAsRead: () => void
    onCloseAsRead: () => void
    onSelectAll: (selectAll: boolean) => void
    teamMembers: TeamMember[]
    selectedConversations: string[]
    conversationsData: any[]
}

interface ConfirmationModalProps {
    show: boolean
    title: string
    message: string
    confirmText: string
    cancelText: string
    onConfirm: () => void
    onCancel: () => void
    isDestructive?: boolean
    isLoading?: boolean
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    show,
    title,
    message,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    isDestructive = false,
    isLoading = false,
}) => {
    return (
        <Modal show={show} onHide={onCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title className={clsx('fs-5', isDestructive && 'text-danger')}>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className='mb-0'>{message}</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='light' onClick={onCancel} disabled={isLoading}>
                    {cancelText}
                </Button>
                <Button
                    variant={isDestructive ? 'danger' : 'primary'}
                    onClick={onConfirm}
                    disabled={isLoading}
                >
                    {isLoading && (
                        <Spinner
                            as='span'
                            animation='border'
                            size='sm'
                            role='status'
                            aria-hidden='true'
                            className='me-2'
                        />
                    )}
                    {confirmText}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

interface TagSelectionModalProps {
    show: boolean
    onClose: () => void
    onConfirm: (selectedTags: any[]) => void
    selectedCount: number
    selectedConversations: string[]
    conversationsData: any[]
}

const TagSelectionModal: React.FC<TagSelectionModalProps> = ({
    show,
    onClose,
    onConfirm,
    selectedCount,
    selectedConversations,
    conversationsData,
}) => {
    const dispatch = useDispatch()
    const [selectedTags, setSelectedTags] = useState<any[]>([])
    const tagslist = useSelector((state: any) => state?.tags?.allTags?.data)
    const isDefaultRegion = useSelector(getIsDefaultRegion)

    const { tagRegion } = useFetchTagQuery(
        { type: 'tag', limit: 1000 },
        {
            selectFromResult: ({ data }) => {
                return {
                    tagRegion: data
                        ? data?.data?.items
                            ?.filter((val: any) => val.type !== 'list' && val?.isHidden === false)
                            .map((val: any) => ({ value: val?._id, label: val?.name }))
                        : [],
                }
            },
            skip: isDefaultRegion,
        }
    )

    const [availableTags, setAvailableTags] = useState<any[]>([])

    useEffect(() => {
        dispatch(getalltagsdata())
    }, [dispatch])

    useEffect(() => {
        const filteredTags = tagslist?.filter((val: any) => {
            if (val?.type) {
                return val?.isHidden === false && val?.type !== 'list'
            }
            return val?.isHidden === false
        })
        const listData = filteredTags?.map((val: any) => ({ value: val?._id, label: val?.name }))
        setAvailableTags(listData || [])
    }, [tagslist])

    const handleTagChange = (selectedOptions: any) => {
        setSelectedTags(selectedOptions || [])
    }

    const handleConfirm = () => {
        onConfirm(selectedTags)
        setSelectedTags([])
        onClose()
    }

    const getSelectedConversationNames = () => {
        return selectedConversations.map(id => {
            const conversation = conversationsData.find(item => item.contacts._id === id)
            if (conversation) {
                const name = conversation?.contactgroupmaps?.fields
                    ?.filter((it: any) => ['First Name', 'Last Name'].includes(it?.name))
                    ?.map((it: any) => it?.value)
                    ?.join(' ') || conversation?.receiver
                return name || conversation?.receiver
            }
            return id
        })
    }

    return (
        <Modal show={show} onHide={onClose} dialogClassName='modal-dialog-centered mw-600px'>
            <div className='modal-header'>
                <h2>Add Tags to Conversations</h2>
                <div className='btn btn-icon btn-sm' onClick={onClose}>
                    <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
                </div>
            </div>
            <div className='modal-body'>
                {/* Selected Conversations Display */}
                <div className='mb-5'>
                    <label className='col-form-label fw-bold fs-6 mb-2'>Selected Conversations ({selectedCount})</label>
                    <div className='border rounded p-3' style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {getSelectedConversationNames().map((name, index) => (
                            <div key={index} className='d-flex align-items-center mb-2'>
                                <KTSVG path='/media/icons/duotune/communication/com012.svg' className='svg-icon-4 me-2' />
                                <span className='fs-7'>{name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tag Selection */}
                <div>
                    <label className='col-form-label fw-bold fs-6 mb-2'>Select Tags</label>
                    <Select
                        value={selectedTags}
                        isMulti
                        className='react-tags-selectbox'
                        classNamePrefix='react-select'
                        options={isDefaultRegion ? availableTags : tagRegion}
                        onChange={handleTagChange}
                        placeholder='Choose tags to apply...'
                        isClearable
                        isSearchable
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: (base: any) => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}
                    />
                </div>
            </div>
            <div className='modal-footer d-flex justify-content-end m-0'>
                <button
                    onClick={() => {
                        onClose()
                        setSelectedTags([])
                    }}
                    type='button'
                    className='btn btn-light-primary me-3'
                >
                    Cancel
                </button>
                <button
                    onClick={handleConfirm}
                    type='button'
                    className='btn btn-primary'
                    disabled={selectedTags.length === 0}
                >
                    Add {selectedTags.length} Tag{selectedTags.length !== 1 ? 's' : ''} to {selectedCount} Conversation{selectedCount !== 1 ? 's' : ''}
                </button>
            </div>
        </Modal>
    )
}

const BulkActionsDropdown: React.FC<BulkActionsDropdownProps> = ({
    show,
    onClose,
    selectedCount,
    totalCount,
    onAssignTo,
    onAddTag,
    onUnsubscribe,
    onArchive,
    onFlag,
    onMarkAsRead,
    onCloseAsRead,
    onSelectAll,
    teamMembers,
    selectedConversations,
    conversationsData,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Use the bulk operations hook
    const {
        state: bulkState,
        assignConversations,
        archiveConversations,
        unsubscribeConversations,
        addTagsToConversations,
        markConversationsAsRead,
        markConversationsAsUnread,
        closeConversationsAsRead,
        flagConversations,
        restoreConversations,
    } = useBulkOperations({
        refreshOnSuccess: true,
        showSuccessToast: true,
        showErrorToast: true,
        clearSelectionOnSuccess: true
    })

    // State for confirmation modal
    const [confirmationModal, setConfirmationModal] = useState<{
        show: boolean
        title: string
        message: string
        action: string
        isDestructive: boolean
        assignee?: any
        selectedTags?: any[]
    }>({
        show: false,
        title: '',
        message: '',
        action: '',
        isDestructive: false,
        assignee: null,
        selectedTags: [],
    })

    // State for tag selection modal
    const [showTagModal, setShowTagModal] = useState(false)


    // Loading states for different actions
    const [loadingStates, setLoadingStates] = useState({
        assign: false,
        addTag: false,
        markAsRead: false,
        markAsUnread: false,
        closeAsRead: false,
        unsubscribe: false,
        archive: false,
        unarchive: false,
        flag: false,
        unflag: false,
    })

    // Calculate select all checkbox state
    const selectAllState = React.useMemo(() => {
        if (selectedCount === 0) return 'none'
        if (selectedCount === totalCount) return 'all'
        return 'some'
    }, [selectedCount, totalCount])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node

            // Don't close if clicking inside the dropdown
            if (dropdownRef.current && dropdownRef.current.contains(target)) {
                return
            }

            // Don't close if clicking inside any modal (confirmation or tag selection)
            const modalElement = document.querySelector('.modal')
            if (modalElement && modalElement.contains(target)) {
                return
            }

            // Don't close if clicking inside React Select dropdown
            const reactSelectMenu = document.querySelector('.react-select__menu')
            if (reactSelectMenu && reactSelectMenu.contains(target)) {
                return
            }

            // Don't close if React Select menu portal exists and contains target
            const reactSelectPortal = document.querySelector('.react-select__menu-portal')
            if (reactSelectPortal && reactSelectPortal.contains(target)) {
                return
            }

            // Don't close if confirmation modal is open
            if (confirmationModal.show) {
                return
            }

            // Don't close if tag selection modal is open
            if (showTagModal) {
                return
            }

            onClose()
        }

        if (show) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [show, onClose, confirmationModal.show, showTagModal])

    // Handle confirmation actions
    const handleConfirmAction = (action: string, title: string, message: string, isDestructive = false) => {
        setConfirmationModal({
            show: true,
            title,
            message,
            action,
            isDestructive,
        })
    }

    const executeConfirmedAction = async () => {
        const action = confirmationModal.action
        setLoadingStates(prev => ({ ...prev, [action]: true }))

        try {
            switch (action) {
                case 'assign':
                    if (confirmationModal.assignee) {
                        await assignConversations(selectedConversations, confirmationModal.assignee._id)
                    }
                    break
                case 'addTag':
                    if (confirmationModal.selectedTags && confirmationModal.selectedTags.length > 0) {
                        const tagIds = confirmationModal.selectedTags.map((tag: any) => tag.value)
                        await addTagsToConversations(selectedConversations, tagIds)
                    }
                    break
                case 'markAsRead':
                    await markConversationsAsRead(selectedConversations)
                    break
                case 'markAsUnread':
                    await markConversationsAsUnread(selectedConversations)
                    break
                case 'closeAsRead':
                    await closeConversationsAsRead(selectedConversations)
                    break
                case 'unsubscribe':
                    await unsubscribeConversations(selectedConversations)
                    break
                case 'archive':
                    await archiveConversations(selectedConversations)
                    break
                case 'flag':
                    await flagConversations(selectedConversations)
                    break
                case 'unarchive':
                    await restoreConversations(selectedConversations)
                    break
                case 'unflag':
                    await flagConversations(selectedConversations, false)
                    break
                default:
                    break
            }
        } catch (error) {
            console.error(`Error executing ${action}:`, error)
        } finally {
            setLoadingStates(prev => ({ ...prev, [action]: false }))
        }

        setConfirmationModal(prev => ({ ...prev, show: false, assignee: null, selectedTags: [] }))
        onClose()
    }

    // Handle select all checkbox change
    const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked
        onSelectAll(isChecked)
    }

    if (!show) return null

    const ActionItem: React.FC<{
        icon: string
        label: string
        onClick: () => void
        className?: string
        isLoading?: boolean
        tooltip?: string
    }> = ({ icon, label, onClick, className, isLoading = false, tooltip }) => {
        const actionItem = (
            <div
                className={clsx(
                    'dropdown-item d-flex align-items-center px-3 py-2 cursor-pointer',
                    'hover-bg-light-primary hover-text-primary',
                    className,
                    isLoading && 'pe-none opacity-50'
                )}
                onClick={!isLoading ? onClick : undefined}
                style={{
                    transition: 'all 0.2s ease',
                }}
            >
                <div className='symbol symbol-20px me-3'>
                    {isLoading ? (
                        <Spinner size='sm' className='text-primary' />
                    ) : (
                        <KTSVG path={icon} className='svg-icon-2' />
                    )}
                </div>
                <span className='fs-7 fw-bold'>{label}</span>
            </div>
        )

        return tooltip ? (
            <OverlayTrigger
                placement='left'
                overlay={<Tooltip id={`tooltip-${label.toLowerCase().replace(/\s+/g, '-')}`}>{tooltip}</Tooltip>}
            >
                {actionItem}
            </OverlayTrigger>
        ) : actionItem
    }

    return (
        <>
            <div
                ref={dropdownRef}
                className={clsx('position-absolute', styles.dropdown)}
                style={{
                    top: '100%',
                    left: 0,
                    zIndex: 1050,
                    minWidth: '250px'
                }}
            >
                <div className={styles.dropdownContent}>
                    {/* Header with selected count and select all */}
                    <div className={clsx('d-flex align-items-center justify-content-between p-3 border-bottom', styles.dropdownHeader)}>
                        <div className='d-flex align-items-center'>
                            <div className='form-check me-3'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    id='selectAllCheckbox'
                                    checked={selectAllState === 'all'}
                                    ref={(input) => {
                                        if (input) input.indeterminate = selectAllState === 'some'
                                    }}
                                    onChange={handleSelectAllChange}
                                />
                                <label className='form-check-label fw-bold fs-7' htmlFor='selectAllCheckbox'>
                                    Select All
                                </label>
                            </div>
                        </div>
                        <span className='badge badge-light-primary fs-8'>
                            {selectedCount} selected
                        </span>
                    </div>
                    {/* Assign To - with submenu */}
                    <div className={styles.submenuContainer}>
                        <div className={clsx('dropdown-item d-flex align-items-center px-3 py-2 cursor-pointer hover-bg-light-primary hover-text-primary justify-content-between', styles.actionItem)}>
                            <div className='d-flex align-items-center'>
                                <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-3 me-3' />
                                <span className={clsx(styles.actionLabel, 'fs-7 fw-bold')}>Assign to</span>
                            </div>
                            <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-4' />
                        </div>

                        {/* Team Members Submenu */}
                        <div className={styles.submenu}>
                            {teamMembers.map((member) => (
                                <button
                                    key={member._id}
                                    className={clsx('d-flex align-items-center w-100 p-2', styles.submenuItem)}
                                    onClick={() => {
                                        setConfirmationModal({
                                            show: true,
                                            title: 'Confirm Assignment',
                                            message: `Are you sure you want to assign ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} to ${member.name}?`,
                                            action: 'assign',
                                            isDestructive: false,
                                            assignee: member,
                                        })
                                    }}
                                    disabled={loadingStates.assign}
                                >
                                    <div className='symbol symbol-25px symbol-circle me-3'>
                                        {member.avatar ? (
                                            <img src={member.avatar} alt={member.name} className='symbol-label' />
                                        ) : (
                                            <span
                                                className='symbol-label fs-7 fw-bolder'
                                                style={{ backgroundColor: '#F4F4F4', color: '#404253' }}
                                            >
                                                {member.name.charAt(0).toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <div className='flex-grow-1'>
                                        <div className='fw-bold fs-7'>{member.name}</div>
                                        <div className='text-muted fs-8'>{member.email}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <ActionItem
                        icon='/media/icons/duotune/communication/com010.svg'
                        label='Add Tag'
                        onClick={() => setShowTagModal(true)}
                        isLoading={loadingStates.addTag}
                        tooltip={`Add ${selectedCount} tag${selectedCount > 1 ? 's' : ''} to ${selectedCount} conversation${selectedCount !== 1 ? 's' : ''}`}
                    />

                    <ActionItem
                        icon='/media/icons/duotune/general/gen043.svg'
                        label='Mark as Read'
                        onClick={() => handleConfirmAction(
                            'markAsRead',
                            'Confirm Mark as Read',
                            `Are you sure you want to mark ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} as read?`,
                            false
                        )}
                        isLoading={loadingStates.markAsRead}
                        tooltip={`Mark ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} as read`}
                    />

                    <ActionItem
                        icon='/media/icons/duotune/general/gen050.svg'
                        label='Mark as Unread'
                        onClick={() => handleConfirmAction(
                            'markAsUnread',
                            'Confirm Mark as Unread',
                            `Are you sure you want to mark ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} as unread?`,
                            false
                        )}
                        isLoading={loadingStates.markAsUnread}
                        tooltip={`Mark ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} as unread`}
                    />

                    <ActionItem
                        icon='/media/icons/duotune/general/gen044.svg'
                        label='Close as Read'
                        onClick={() => handleConfirmAction(
                            'closeAsRead',
                            'Confirm Close as Read',
                            `Are you sure you want to close ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} as read?`,
                            false
                        )}
                        isLoading={loadingStates.closeAsRead}
                        tooltip={`Close ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} as read`}
                    />

                    <ActionItem
                        icon='/media/icons/duotune/general/gen040.svg'
                        label='Unsubscribe'
                        onClick={() => handleConfirmAction(
                            'unsubscribe',
                            'Confirm Unsubscribe',
                            `Are you sure you want to unsubscribe ${selectedCount} conversation${selectedCount > 1 ? 's' : ''}?`,
                            true
                        )}
                        isLoading={loadingStates.unsubscribe}
                        tooltip={`Unsubscribe ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} from future messages`}
                    />

                    <ActionItem
                        icon='/media/icons/duotune/files/fil003.svg'
                        label='Archive'
                        onClick={() => handleConfirmAction(
                            'archive',
                            'Confirm Archive',
                            `Are you sure you want to archive ${selectedCount} conversation${selectedCount > 1 ? 's' : ''}?`,
                            false
                        )}
                        isLoading={loadingStates.archive}
                        tooltip={`Archive ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} to remove from active inbox`}
                    />

                    <ActionItem
                        icon='/media/icons/duotune/general/gen056.svg'
                        label='Flag'
                        onClick={() => handleConfirmAction(
                            'flag',
                            'Confirm Flag',
                            `Are you sure you want to flag ${selectedCount} conversation${selectedCount > 1 ? 's' : ''}?`,
                            false
                        )}
                        isLoading={loadingStates.flag}
                        tooltip={`Flag ${selectedCount} conversation${selectedCount > 1 ? 's' : ''} for follow-up`}
                    />
                </div>
            </div>
            {/* Confirmation Modal - Render outside dropdown to avoid z-index issues */}
            {confirmationModal.show && (
                <ConfirmationModal
                    show={confirmationModal.show}
                    title={confirmationModal.title}
                    message={confirmationModal.message}
                    confirmText={
                        confirmationModal.action === 'unsubscribe' ? 'Unsubscribe' :
                            confirmationModal.action === 'archive' ? 'Archive' :
                                confirmationModal.action === 'assign' ? 'Assign' :
                                    confirmationModal.action === 'addTag' ? 'Add Tag' :
                                        confirmationModal.action === 'markAsRead' ? 'Mark as Read' :
                                            confirmationModal.action === 'markAsUnread' ? 'Mark as Unread' :
                                                confirmationModal.action === 'closeAsRead' ? 'Close as Read' :
                                                    confirmationModal.action === 'flag' ? 'Flag' :
                                                        'Confirm'
                    }
                    cancelText='Cancel'
                    onConfirm={executeConfirmedAction}
                    onCancel={() => setConfirmationModal(prev => ({ ...prev, show: false, assignee: null, selectedTags: [] }))}
                    isDestructive={confirmationModal.isDestructive}
                    isLoading={loadingStates[confirmationModal.action as keyof typeof loadingStates]}
                />
            )}

            {/* Tag Selection Modal */}
            <TagSelectionModal
                show={showTagModal}
                onClose={() => setShowTagModal(false)}
                onConfirm={(selectedTags) => {
                    setConfirmationModal({
                        show: true,
                        title: 'Confirm Add Tags',
                        message: `Are you sure you want to add ${selectedTags.length} tag${selectedTags.length !== 1 ? 's' : ''} to ${selectedCount} conversation${selectedCount !== 1 ? 's' : ''}?`,
                        action: 'addTag',
                        isDestructive: false,
                        selectedTags: selectedTags,
                    })
                }}
                selectedCount={selectedCount}
                selectedConversations={selectedConversations}
                conversationsData={conversationsData}
            />
        </>
    )
}

export default BulkActionsDropdown
